/**
 * FinanceApp - Gestion d'équipe (invitations, rôles, sièges)
 * Copyright (c) 2026 Emmanuel TESTE
 */
import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import { useAuth } from "../contexts/AuthContext";

const ROLES = [
  { value: "admin",        label: "Administrateur" },
  { value: "comptable",    label: "Comptable" },
  { value: "gestionnaire", label: "Gestionnaire" },
  { value: "logisticien",  label: "Logisticien" },
  { value: "readonly",     label: "Lecture seule" },
];

export default function Equipe() {
  const { user } = useAuth();
  const [members, setMembers] = useState([]);
  const [seats, setSeats]     = useState({ used: 0, max: null, plan_slug: "" });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [form, setForm]               = useState({ email: "", name: "", role: "gestionnaire" });
  const [inviting, setInviting]       = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [newAccount, setNewAccount]   = useState(null);

  const [busyId, setBusyId] = useState(null);

  const [permMember, setPermMember]   = useState(null);
  const [permList, setPermList]       = useState([]);
  const [permLoading, setPermLoading] = useState(false);
  const [permBusy, setPermBusy]       = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    api.team.members()
      .then(res => { setMembers(res.members || []); setSeats(res.seats || {}); })
      .catch(err => setError(err.message || "Erreur de chargement."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const isAdmin  = user?.role === "admin";
  const seatFull = seats.max != null && seats.used >= seats.max;

  const handleInvite = async (e) => {
    e.preventDefault();
    setInviteError("");
    setNewAccount(null);
    if (!form.email || !form.name) {
      setInviteError("Email et nom sont requis.");
      return;
    }
    setInviting(true);
    try {
      const res = await api.team.invite(form);
      setNewAccount({ email: res.user.email, temporary_password: res.temporary_password, name: res.user.name });
      setForm({ email: "", name: "", role: "gestionnaire" });
      load();
    } catch (err) {
      setInviteError(err.message || "Erreur lors de l'invitation.");
    } finally {
      setInviting(false);
    }
  };

  const handleRoleChange = async (id, role) => {
    setBusyId(id);
    try {
      await api.team.updateRole(id, role);
      load();
    } catch (err) {
      window.alert(err.message || "Erreur lors du changement de rôle.");
    } finally {
      setBusyId(null);
    }
  };

  const handleToggleActive = async (member) => {
    if (member.id === user?.id) {
      window.alert("Impossible de désactiver votre propre compte.");
      return;
    }
    const action = member.is_active ? "désactiver" : "réactiver";
    if (!window.confirm(`Confirmer : ${action} l'accès de ${member.name} ?`)) return;
    setBusyId(member.id);
    try {
      if (member.is_active) await api.team.deactivate(member.id);
      else await api.team.reactivate(member.id);
      load();
    } catch (err) {
      window.alert(err.message || "Erreur.");
    } finally {
      setBusyId(null);
    }
  };

  const openPermissions = async (member) => {
    setPermMember(member);
    setPermLoading(true);
    try {
      const res = await api.team.getPermissions(member.id);
      setPermList(res.permissions || []);
    } catch (err) {
      window.alert(err.message || "Erreur lors du chargement des permissions.");
      setPermMember(null);
    } finally {
      setPermLoading(false);
    }
  };

  const closePermissions = () => { setPermMember(null); setPermList([]); };

  const togglePermission = async (key, currentGranted) => {
    setPermBusy(key);
    try {
      await api.team.setPermission(permMember.id, key, !currentGranted);
      setPermList(list => list.map(p => p.key === key ? { ...p, granted: !currentGranted } : p));
    } catch (err) {
      window.alert(err.message || "Erreur lors de la mise à jour de la permission.");
    } finally {
      setPermBusy(null);
    }
  };

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Chargement…</div>;

  if (!isAdmin) {
    return (
      <div>
        <div className="page-header"><h2 className="page-title">Gestion d'équipe</h2></div>
        <div className="alert info">Cette section est réservée aux administrateurs de la société.</div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Gestion d'équipe</h2>
        <button className="btn" onClick={load}>🔄 Actualiser</button>
      </div>

      {error && <div className="alert danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="kpi-grid k4">
        <div className="kpi">
          <div className="kpi-label">Sièges utilisés</div>
          <div className="kpi-value" style={{ color: seatFull ? "var(--red)" : "var(--g)" }}>
            {seats.used} / {seats.max ?? "∞"}
          </div>
        </div>
        <div className="kpi"><div className="kpi-label">Plan</div><div className="kpi-value" style={{ textTransform: "capitalize" }}>{seats.plan_slug || "—"}</div></div>
        <div className="kpi"><div className="kpi-label">Membres actifs</div><div className="kpi-value">{members.filter(m => m.is_active).length}</div></div>
        <div className="kpi"><div className="kpi-label">Comptes désactivés</div><div className="kpi-value">{members.filter(m => !m.is_active).length}</div></div>
      </div>

      <div className="g2">
        <div className="card">
          <div className="card-header"><span className="card-title">➕ Inviter un collaborateur</span></div>

          {seatFull && (
            <div className="alert danger" style={{ marginBottom: 12 }}>
              Limite de sièges atteinte ({seats.used}/{seats.max}). Contactez le support pour augmenter votre plan.
            </div>
          )}
          {inviteError && <div className="alert danger" style={{ marginBottom: 12 }}>{inviteError}</div>}

          {newAccount && (
            <div className="alert success" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 700, marginBottom: 4 }}>Compte créé pour {newAccount.name}</div>
              <div style={{ fontSize: 12 }}>
                Email : <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>{newAccount.email}</code><br/>
                Mot de passe temporaire : <code style={{ background: "#e5e7eb", padding: "1px 4px", borderRadius: 3 }}>{newAccount.temporary_password}</code>
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 6 }}>
                Communiquez ces identifiants au collaborateur. Il devra changer son mot de passe à la première connexion. Ce mot de passe ne sera plus affiché ensuite.
              </div>
            </div>
          )}

          <form onSubmit={handleInvite}>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Nom complet</label>
              <input className="form-control" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Jean Dupont" disabled={seatFull} />
            </div>
            <div className="form-group" style={{ marginBottom: 10 }}>
              <label className="form-label">Email</label>
              <input className="form-control" type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="jean.dupont@societe.fr" disabled={seatFull} />
            </div>
            <div className="form-group" style={{ marginBottom: 14 }}>
              <label className="form-label">Rôle</label>
              <select className="form-control" value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} disabled={seatFull}>
                {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <button className="btn primary" type="submit" disabled={inviting || seatFull} style={{ width: "100%" }}>
              {inviting ? "Création…" : "Créer le compte"}
            </button>
          </form>
        </div>

        <div className="card">
          <div className="card-header"><span className="card-title">👥 Membres de l'équipe</span></div>
          <div style={{ maxHeight: 460, overflowY: "auto" }}>
            {members.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: 16 }}>Aucun membre.</div>
            ) : members.map(m => (
              <div key={m.id} style={{ padding: "10px 0", borderBottom: "0.5px solid #f3f4f6", display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: m.is_active ? "#1a9e72" : "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, color: "#fff", flexShrink: 0 }}>
                  {m.name?.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase() || "??"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    {m.name}
                    {m.id === user?.id && <span style={{ fontSize: 10, color: "var(--text-muted)" }}>(vous)</span>}
                    {!m.is_active && <span className="badge" style={{ fontSize: 9, background: "#f3f4f6", color: "#6b7280" }}>Désactivé</span>}
                    {m.must_change_password === 1 && m.is_active === 1 && <span className="badge" style={{ fontSize: 9, background: "#fef3c7", color: "#b45309" }}>1ère connexion en attente</span>}
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{m.email}</div>
                </div>
                <select
                  className="form-control"
                  style={{ width: 140, fontSize: 11, height: 30, padding: "0 8px" }}
                  value={m.role}
                  disabled={busyId === m.id || m.id === user?.id}
                  onChange={e => handleRoleChange(m.id, e.target.value)}
                >
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
                {m.role !== "admin" && (
                  <button
                    className="btn"
                    style={{ fontSize: 11, padding: "5px 10px" }}
                    onClick={() => openPermissions(m)}
                  >
                    🔐 Permissions
                  </button>
                )}
                <button
                  className="btn"
                  style={{ fontSize: 11, padding: "5px 10px", color: m.is_active ? "var(--red)" : "var(--g)" }}
                  disabled={busyId === m.id || m.id === user?.id}
                  onClick={() => handleToggleActive(m)}
                >
                  {m.is_active ? "Révoquer" : "Réactiver"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {permMember && (
        <div
          onClick={closePermissions}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
            display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000,
          }}
        >
          <div onClick={e => e.stopPropagation()} className="card" style={{ width: 420, maxWidth: "90vw" }}>
            <div className="card-header" style={{ marginBottom: 12 }}>
              <span className="card-title">🔐 Permissions — {permMember.name}</span>
            </div>
            {permLoading ? (
              <div style={{ textAlign: "center", padding: 30, color: "var(--text-muted)" }}>Chargement…</div>
            ) : permList.length === 0 ? (
              <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Aucune permission disponible pour le moment.</div>
            ) : (
              <div>
                {permList.map(p => (
                  <div key={p.key} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: "0.5px solid #f3f4f6" }}>
                    <input
                      type="checkbox"
                      checked={p.granted}
                      disabled={permBusy === p.key}
                      onChange={() => togglePermission(p.key, p.granted)}
                      style={{ width: 16, height: 16, marginTop: 2, accentColor: "var(--g)", cursor: "pointer", flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12.5, fontWeight: 600 }}>{p.label}</div>
                      <div style={{ fontSize: 11, color: "var(--text-muted)" }}>{p.description}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginTop: 16, textAlign: "right" }}>
              <button className="btn" onClick={closePermissions}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
